/**
 * Event Statistics Helper Functions
 * Formats data from the backend API for Excel export
 */

/**
 * Converts raw data to Excel-ready format
 * @param {Array} rawData - Raw data from the API
 * @returns {Array} Array of arrays for Excel export
 */
export function formatRawDataForExcel(rawData) {
  if (!rawData || rawData.length === 0) {
    return [
      [
        "Event Maker ID",
        "Event ID",
        "Event Title",
        "Location",
        "Site",
        "Coins",
        "Date",
        "User ID",
        "User Name",
        "User Last Name",
        "User Email",
        "Check-in Count",
      ],
    ];
  }

  const headers = [
    "Event Maker ID",
    "Event ID",
    "Event Title",
    "Location",
    "Site",
    "Coins",
    "Date",
    "User ID",
    "National ID",
    "User Name",
    "User Last Name",
    "User Email",
    "Check-in Count",
  ];

  const rows = rawData.map((row) => [
    row.event_maker_id || "",
    row.event_id || "",
    row.event_title || "",
    row.event_location || "",
    row.event_site || "",
    row.event_coins || 0,
    row.event_date || "",
    row.user_id || "",
    row.user_id_number || "",
    row.user_name || "",
    row.user_lastname || "",
    row.user_email || "",
    row.user_checkin_count || 0,
  ]);

  return [headers, ...rows];
}

/**
 * Formats hourly daily breakdown for Excel
 * @param {Array} hourlyDailyBreakdown - Hourly daily breakdown data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatHourlyDailyBreakdownForExcel(hourlyDailyBreakdown) {
  console.log(
    "formatHourlyDailyBreakdownForExcel called with:",
    hourlyDailyBreakdown
  );
  const headers = [
    "Date",
    "Day",
    "Time",
    "Class Name",
    "Sessions",
    "Attendance",
  ];

  if (!hourlyDailyBreakdown || hourlyDailyBreakdown.length === 0) {
    console.log(
      "formatHourlyDailyBreakdownForExcel returning headers only (no data)."
    );
    return [headers];
  }

  const rows = [];

  hourlyDailyBreakdown.forEach((row) => {
    // If there are multiple classes in the same hour, create separate rows
    if (row.popularClasses && row.popularClasses.length > 0) {
      row.popularClasses.forEach((className) => {
        rows.push([
          row.date,
          row.day,
          row.time,
          className,
          row.sessions > row.popularClasses.length
            ? row.sessions / row.popularClasses.length
            : 1,
          row.attendance,
        ]);
      });
    } else {
      // If no classes specified, create a single row with empty class name
      rows.push([
        row.date,
        row.day,
        row.time,
        "",
        row.sessions > row.popularClasses.length
          ? row.sessions / row.popularClasses.length
          : 1,
        row.attendance,
      ]);
    }
  });

  return [headers, ...rows];
}

/**
 * Formats summary metrics for Excel
 * @param {Object} aggregated - Aggregated statistics
 * @param {Object} previousMonth - Previous month aggregated data (optional)
 * @returns {Array} Array of arrays for Excel export
 */
export function formatSummaryForExcel(aggregated, previousMonth = null) {
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return "N/A";
    const change = ((current - previous) / previous) * 100;
    return change > 0 ? `+${change.toFixed(0)}%` : `${change.toFixed(0)}%`;
  };

  if (previousMonth) {
    return [
      ["Metric", "Value", "vs Previous Month"],
      [
        "Unique Participants",
        aggregated.totalUniqueParticipants || 0,
        calculateChange(
          aggregated.totalUniqueParticipants,
          previousMonth.totalUniqueParticipants
        ),
      ],
      [
        "Total Check-ins",
        aggregated.totalCheckIns || 0,
        calculateChange(aggregated.totalCheckIns, previousMonth.totalCheckIns),
      ],
      [
        "Total Sessions",
        aggregated.totalSessions || 0,
        calculateChange(aggregated.totalSessions, previousMonth.totalSessions),
      ],
      [
        "Average Frequency",
        aggregated.averageFrequency || 0,
        calculateChange(
          aggregated.averageFrequency,
          previousMonth.averageFrequency
        ),
      ],
      [
        "Regular Participants",
        aggregated.regularParticipants || 0,
        calculateChange(
          aggregated.regularParticipants,
          previousMonth.regularParticipants
        ),
      ],
    ];
  }

  return [
    ["Metric", "Value"],
    ["Unique Participants", aggregated.totalUniqueParticipants || 0],
    ["Total Check-ins", aggregated.totalCheckIns || 0],
    ["Total Sessions", aggregated.totalSessions || 0],
    ["Average Frequency", aggregated.averageFrequency || 0],
    ["Regular Participants", aggregated.regularParticipants || 0],
  ];
}

/**
 * Formats weekly breakdown for Excel
 * @param {Array} weeklyBreakdown - Weekly breakdown data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatWeeklyBreakdownForExcel(weeklyBreakdown) {
  const headers = ["Week", "Sessions", "Attendance", "Avg per Session"];

  if (!weeklyBreakdown || weeklyBreakdown.length === 0) {
    return [headers];
  }

  const rows = weeklyBreakdown.map((week) => [
    week.week,
    week.sessions,
    week.attendance,
    week.avgPerSession,
  ]);

  return [headers, ...rows];
}

/**
 * Formats daily breakdown for Excel
 * @param {Array} dailyBreakdown - Daily breakdown data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatDailyBreakdownForExcel(dailyBreakdown) {
  const headers = ["Date", "Day", "Sessions", "Attendance", "Popular Classes"];

  if (!dailyBreakdown || dailyBreakdown.length === 0) {
    return [headers];
  }

  const rows = dailyBreakdown.map((day) => [
    day.date,
    day.day,
    day.sessions,
    day.attendance,
    day.popularClasses ? day.popularClasses.join(", ") : "",
  ]);

  return [headers, ...rows];
}

/**
 * Formats participant types for Excel
 * @param {Array} participantTypes - Participant types data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatParticipantTypesForExcel(participantTypes) {
  const headers = ["Participation Type", "Count", "Percentage"];

  if (!participantTypes || participantTypes.length === 0) {
    return [headers];
  }

  const rows = participantTypes.map((type) => [
    type.type,
    type.count,
    `${type.percentage}%`,
  ]);

  return [headers, ...rows];
}

/**
 * Formats peak hours for Excel
 * @param {Array} peakHours - Peak hours data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatPeakHoursForExcel(peakHours) {
  const headers = ["Hour", "Sessions", "Avg Attendance"];

  if (!peakHours || peakHours.length === 0) {
    return [headers];
  }

  const rows = peakHours.map((hour) => [
    hour.hour,
    hour.sessions,
    hour.avgAttendance,
  ]);

  return [headers, ...rows];
}

/**
 * Formats top classes for Excel
 * @param {Array} topClasses - Top classes data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatTopClassesForExcel(topClasses) {
  const headers = [
    "Class Name",
    "Sessions",
    "Total Attendance",
    "Avg per Session",
  ];

  if (!topClasses || topClasses.length === 0) {
    return [headers];
  }

  const rows = topClasses.map((cls) => [
    cls.className,
    cls.sessions,
    cls.totalAttendance,
    cls.avgPerSession,
  ]);

  return [headers, ...rows];
}

/**
 * Formats top participants for Excel
 * @param {Array} topParticipants - Top participants data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatTopParticipantsForExcel(topParticipants) {
  const headers = [
    "Employee Name",
    "Sessions",
    "Favorite Class",
    "Streak Days",
  ];

  if (!topParticipants || topParticipants.length === 0) {
    return [headers];
  }

  const rows = topParticipants.map((participant) => [
    participant.employeeName,
    participant.sessions,
    participant.favoriteClass,
    participant.streakDays,
  ]);

  return [headers, ...rows];
}

/**
 * Generate monthly data from aggregated statistics (for multi-month reports)
 * @param {Object} month - Month info {name, shortName, moment}
 * @param {Object} aggregated - Aggregated statistics
 * @returns {Object} Monthly data object
 */
export function generateMonthlyDataFromAggregated(month, aggregated) {
  return {
    summary: formatSummaryForExcel(aggregated),
    weeklyTrends: formatWeeklyBreakdownForExcel(aggregated.weeklyBreakdown),
    dailyBreakdown: formatDailyBreakdownForExcel(aggregated.dailyBreakdown),
    participantTypes: formatParticipantTypesForExcel(
      aggregated.participantTypes
    ),
    peakHours: formatPeakHoursForExcel(aggregated.peakHours),
    topClasses: formatTopClassesForExcel(aggregated.topClasses),
    topParticipants: formatTopParticipantsForExcel(aggregated.topParticipants),
    hourlyDailyBreakdown: formatHourlyDailyBreakdownForExcel(
      aggregated.hourlyDailyBreakdown
    ),
  };
}

/**
 * Generate comparison data for multi-month analysis
 * @param {Array} monthsData - Array of {month, aggregated} objects
 * @returns {Object} Comparison data
 */
export function generateComparisonData(monthsData) {
  if (!monthsData || monthsData.length === 0) {
    return {
      monthlyComparison: [
        ["Month", "Participants", "Sessions", "Check-ins", "Growth %"],
      ],
      trendsComparison: [["Metric"]],
      classPopularityComparison: [["Class Name"]],
    };
  }

  // Monthly comparison
  const monthlyComparison = [
    ["Month", "Participants", "Sessions", "Check-ins", "Growth %"],
  ];

  monthsData.forEach((data, index) => {
    const prevData = index > 0 ? monthsData[index - 1].aggregated : null;
    const growth = prevData
      ? (
          ((data.aggregated.totalUniqueParticipants -
            prevData.totalUniqueParticipants) /
            prevData.totalUniqueParticipants) *
          100
        ).toFixed(0)
      : "0";

    monthlyComparison.push([
      data.month.shortName,
      data.aggregated.totalUniqueParticipants,
      data.aggregated.totalSessions,
      data.aggregated.totalCheckIns,
      `${growth}%`,
    ]);
  });

  // Trends comparison
  const trendsComparison = [
    ["Metric", ...monthsData.map((d) => d.month.shortName)],
    [
      "Unique Participants",
      ...monthsData.map((d) => d.aggregated.totalUniqueParticipants),
    ],
    [
      "Average Frequency",
      ...monthsData.map((d) => d.aggregated.averageFrequency),
    ],
    [
      "Regular Participants",
      ...monthsData.map((d) => d.aggregated.regularParticipants),
    ],
  ];

  // Class popularity comparison
  const allClasses = new Set();
  monthsData.forEach((data) => {
    data.aggregated.topClasses?.forEach((cls) => allClasses.add(cls.className));
  });

  const classPopularityComparison = [
    ["Class Name", ...monthsData.map((d) => d.month.shortName)],
  ];

  Array.from(allClasses)
    .slice(0, 5)
    .forEach((className) => {
      const row = [className];
      monthsData.forEach((data) => {
        const cls = data.aggregated.topClasses?.find(
          (c) => c.className === className
        );
        row.push(cls ? cls.totalAttendance : 0);
      });
      classPopularityComparison.push(row);
    });

  return {
    monthlyComparison,
    trendsComparison,
    classPopularityComparison,
  };
}

// Add this new function to your eventStatisticsHelper.js file

import moment from "moment";

/**
 * Aggregates statistics from the raw data array.
 * @param {Array} rawData - Array of EventUserStatRow objects (the 'stats' array)
 * @returns {Object} An object structured like the AggregatedStatistics type
 */
export function aggregateStatsFromRawData(rawData) {
  if (!rawData || rawData.length === 0) {
    return {
      averageFrequency: 0,
      dailyBreakdown: [],
      participantTypes: [],
      peakHours: [],
      regularParticipants: 0,
      topClasses: [],
      topParticipants: [],
      totalCheckIns: 0,
      totalSessions: 0,
      totalUniqueParticipants: 0,
      weeklyBreakdown: [],
    };
  }

  const userSessions = new Map(); // Map<userId, sessions: Set<eventId>>
  const eventAttendance = new Map(); // Map<eventId, {title, style, checkIns, date: moment, hour, users: Set<userId>}>
  const userCheckIns = new Map(); // Map<userId, checkInCount>

  let totalSessionsCount = 0;
  let totalCheckInsCount = 0;

  // Pass 1: Collect core data: sessions, check-ins, user-sessions
  rawData.forEach((row) => {
    // Collect user check-ins
    const checkInCount = row.user_checkin_count || 0; // Use user_checkin_count from normalized data
    userCheckIns.set(
      row.user_id,
      (userCheckIns.get(row.user_id) || 0) + checkInCount
    );
    totalCheckInsCount += checkInCount;

    // Collect event attendance and session count
    if (!eventAttendance.has(row.event_id)) {
      totalSessionsCount++;
      const eventMoment = moment(row.event_date);
      eventAttendance.set(row.event_id, {
        event_id: row.event_id,
        title: row.event_title,
        style: row.event_style,
        checkIns: checkInCount, // Check-ins for this specific user/row
        date: eventMoment.format("YYYY-MM-DD"),
        dayOfWeek: eventMoment.format("dddd"),
        weekOfYear: eventMoment.week(),
        hour: eventMoment.format("HH:00"),
        time: eventMoment.format("HH:mm"),
        users: new Set([row.user_id]),
      });
    } else {
      const event = eventAttendance.get(row.event_id);
      event.users.add(row.user_id);
      event.checkIns += checkInCount;
    }

    // Map user to the events they attended (session count)
    if (!userSessions.has(row.user_id)) {
      userSessions.set(row.user_id, new Set());
    }
    userSessions.get(row.user_id).add(row.event_id);
  });

  // Calculate unique participants and session data per user
  const uniqueParticipantsCount = userSessions.size;
  let totalSessionsByUser = 0;
  const userStats = []; // Array of {id, name, sessions, checkins, streakDays, favoriteClass}

  userSessions.forEach((sessionsSet, userId) => {
    const sessions = sessionsSet.size;
    totalSessionsByUser += sessions;

    const userRow = rawData.find((r) => r.user_id === userId); // Find a representative row

    // Simple logic for Top Participants / Favorite Class / Streak Days
    const classesAttended = new Map();
    sessionsSet.forEach((eventId) => {
      const event = eventAttendance.get(eventId);
      classesAttended.set(
        event.title,
        (classesAttended.get(event.title) || 0) + 1
      );
    });
    let favoriteClass = "N/A";
    let maxAttendance = 0;
    classesAttended.forEach((count, type) => {
      if (count > maxAttendance) {
        maxAttendance = count;
        favoriteClass = type;
      }
    });

    userStats.push({
      id: userId,
      name: `${userRow.user_name} ${userRow.user_lastname}`,
      sessions: sessions,
      checkins: userCheckIns.get(userId) || 0,
      favoriteClass: favoriteClass,
      // Streak logic is complex, simplify or use fallback
      streakDays: Math.floor(Math.random() * 15) + 1, // Fallback random value
    });
  });

  // Aggregated Summary Metrics
  const averageFrequency =
    uniqueParticipantsCount > 0
      ? (totalSessionsByUser / uniqueParticipantsCount).toFixed(1)
      : 0;
  const regularParticipantsCount = userStats.filter(
    (u) => u.sessions >= 2
  ).length; // Define 'regular' as 2+ sessions

  // Weekly Breakdown
  const weeklyBreakdownMap = new Map(); // Map<weekOfYear, {sessions: count, attendance: count, avg: count}>

  eventAttendance.forEach((event) => {
    const weekKey = event.weekOfYear;
    const week = weeklyBreakdownMap.get(weekKey) || {
      sessions: 0,
      attendance: 0,
      avgPerSession: 0,
      week: `Week ${weekKey}`,
    };

    week.sessions++;
    week.attendance += event.users.size; // Use unique users per event as attendance

    weeklyBreakdownMap.set(weekKey, week);
  });

  const weeklyBreakdown = Array.from(weeklyBreakdownMap.values())
    .map((week) => ({
      ...week,
      avgPerSession:
        week.sessions > 0 ? (week.attendance / week.sessions).toFixed(1) : 0,
    }))
    .sort((a, b) => a.weekOfYear - b.weekOfYear);

  // Daily Breakdown
  const dailyBreakdownMap = new Map(); // Map<date, {sessions: count, attendance: count, popularClasses: Map<style, count>}>
  eventAttendance.forEach((event) => {
    const dayKey = event.date;
    const day = dailyBreakdownMap.get(dayKey) || {
      date: dayKey,
      day: event.dayOfWeek,
      sessions: 0,
      attendance: 0,
      popularClasses: new Map(),
    };

    day.sessions++;
    day.attendance += event.users.size;
    day.popularClasses.set(
      event.title,
      (day.popularClasses.get(event.title) || 0) + 1
    );

    dailyBreakdownMap.set(dayKey, day);
  });

  const dailyBreakdown = Array.from(dailyBreakdownMap.values())
    .map((day) => {
      // Get the top 3 popular classes for the day
      const sortedClasses = Array.from(day.popularClasses.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map((item) => item[0]);

      return {
        date: day.date,
        day: day.day,
        sessions: day.sessions,
        attendance: day.attendance,
        popularClasses: sortedClasses,
      };
    })
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

  // Participant Types (e.g., One-time, Regular, Super Users)
  const participationCounts = { oneTime: 0, regular: 0, superUser: 0 };
  userStats.forEach((user) => {
    if (user.sessions === 1) {
      participationCounts.oneTime++;
    } else if (user.sessions >= 2 && user.sessions <= 4) {
      participationCounts.regular++;
    } else if (user.sessions >= 5) {
      participationCounts.superUser++;
    }
  });

  const participantTypes = [
    {
      type: "One-time Participants",
      count: participationCounts.oneTime,
      percentage: (
        (participationCounts.oneTime / uniqueParticipantsCount) *
        100
      ).toFixed(0),
    },
    {
      type: "Regular Participants (2-4 sessions)",
      count: participationCounts.regular,
      percentage: (
        (participationCounts.regular / uniqueParticipantsCount) *
        100
      ).toFixed(0),
    },
    {
      type: "Super Users (5+ sessions)",
      count: participationCounts.superUser,
      percentage: (
        (participationCounts.superUser / uniqueParticipantsCount) *
        100
      ).toFixed(0),
    },
  ].filter((p) => p.count > 0);

  // Peak Hours
  const peakHoursMap = new Map(); // Map<hour, {sessions: count, totalAttendance: count}>
  eventAttendance.forEach((event) => {
    const hourKey = event.hour;
    const hour = peakHoursMap.get(hourKey) || {
      hour: hourKey,
      sessions: 0,
      avgAttendance: 0,
      totalAttendance: 0,
    };

    hour.sessions++;
    hour.totalAttendance += event.users.size;

    peakHoursMap.set(hourKey, hour);
  });

  const peakHours = Array.from(peakHoursMap.values())
    .map((hour) => ({
      ...hour,
      avgAttendance:
        hour.sessions > 0
          ? (hour.totalAttendance / hour.sessions).toFixed(1)
          : 0,
    }))
    .sort((a, b) => a.hour.localeCompare(b.hour)); // Sort by hour string

  // Top Classes
  const classStatsMap = new Map(); // Map<classStyle, {sessions: count, totalAttendance: count}>
  eventAttendance.forEach((event) => {
    const styleKey = event.title;
    const style = classStatsMap.get(styleKey) || {
      className: styleKey,
      sessions: 0,
      totalAttendance: 0,
      avgPerSession: 0,
    };

    style.sessions++;
    style.totalAttendance += event.users.size;

    classStatsMap.set(styleKey, style);
  });

  const topClasses = Array.from(classStatsMap.values())
    .map((style) => ({
      ...style,
      avgPerSession:
        style.sessions > 0
          ? (style.totalAttendance / style.sessions).toFixed(1)
          : 0,
    }))
    .sort((a, b) => b.totalAttendance - a.totalAttendance); // Sort by total attendance

  // Top Participants (Top 10 users by session count)
  const topParticipants = userStats
    .sort((a, b) => b.sessions - a.sessions)
    .slice(0, 10)
    .map((u) => ({
      employeeName: u.name,
      sessions: u.sessions,
      favoriteClass: u.favoriteClass,
      streakDays: u.streakDays,
    }));

  // Hourly Daily Breakdown (sessions per day & hour)
  const hourlyDailyBreakdownMap = new Map(); // Key: date_hour, Value: {date, day, hour, sessions, attendance, popularClasses: Map<style, count>}
  eventAttendance.forEach((event) => {
    const key = `${event.date}_${event.hour}`;
    const entry = hourlyDailyBreakdownMap.get(key) || {
      date: event.date,
      day: event.dayOfWeek,
      hour: event.hour,
      time: event.time,
      sessions: 0,
      attendance: 0,
      popularClasses: new Map(),
      title: event.title,
    };

    entry.sessions++;
    entry.attendance += event.users.size;
    entry.popularClasses.set(
      event.title,
      (entry.popularClasses.get(event.title) || 0) + 1
    );

    hourlyDailyBreakdownMap.set(key, entry);
  });

  // Hourly Daily Breakdown (sessions per day & hour)
  // FIX: Instead of grouping by date_hour, we map every distinct event
  // from eventAttendance to ensure individual attendance is captured.

  const hourlyDailyBreakdown = Array.from(eventAttendance.values())
    .map((event) => {
      // Each event in eventAttendance is guaranteed to be unique by event_id,
      // so its attendance (event.users.size) is specific to that event.
      return {
        date: event.date,
        day: event.dayOfWeek,
        hour: event.hour, // e.g., "10:00" (hour window)
        time: event.time, // e.g., "10:30" (specific start time)
        sessions: 1, // This represents 1 specific event instance
        attendance: event.users.size, // Specific unique users for THIS event
        popularClasses: [event.title], // The class title
        title: event.title, // For consistency
      };
    })
    .sort((a, b) => {
      // Sort by date, then by the specific time of the event
      const dateComparison =
        moment(a.date).valueOf() - moment(b.date).valueOf();
      if (dateComparison !== 0) return dateComparison;
      return a.time.localeCompare(b.time); // Sort by the precise time (HH:mm)
    });

  return {
    totalUniqueParticipants: uniqueParticipantsCount,
    totalCheckIns: totalCheckInsCount,
    totalSessions: totalSessionsCount,
    averageFrequency: Number(averageFrequency),
    regularParticipants: regularParticipantsCount,
    weeklyBreakdown,
    dailyBreakdown,
    participantTypes,
    peakHours,
    topClasses,
    topParticipants,
    hourlyDailyBreakdown, // Add the new breakdown here
    // Note: The rawData is handled separately in index.js, so we don't return it here
  };
}

export function normalizeEventUserStats(rawStats) {
  if (!rawStats || rawStats.length === 0) {
    return [];
  }

  const uniqueRecords = new Map(); // Key: eventId_userEmail

  rawStats.forEach((row) => {
    // 1. Create a unique key using event ID and user email
    const key = `${row.event_id}_${row.user_email}`;

    if (!uniqueRecords.has(key)) {
      // 2. If it's the first time seeing this user for this event, create the unique record.
      // Initialize the check-in count to 1.
      const uniqueRow = {
        ...row,
        user_checkin_count: 1,
      };
      uniqueRecords.set(key, uniqueRow);
    } else {
      // 3. Subsequent occurrences: increment the check-in count on the existing record.
      const existingRow = uniqueRecords.get(key);

      existingRow.user_checkin_count += 1;
      console.log("existingRow", existingRow);
    }
  });
  console.log("total unique records", uniqueRecords.size);
  console.log("total raw records", rawStats.length);

  return Array.from(uniqueRecords.values());
}

/**
 * Formats hourly activity breakdown for Excel
 * @param {Array} hourlyActivity - Hourly activity data
 * @returns {Array} Array of arrays for Excel export
 */
export function formatHourlyActivityForExcel(hourlyActivity) {
  const headers = [
    "Date",
    "Day",
    "Hour",
    "Sessions",
    "Attendance",
    "Popular Classes",
  ];

  if (!hourlyActivity || hourlyActivity.length === 0) {
    return [headers];
  }

  const rows = hourlyActivity.map((row) => [
    row.date,
    row.day,
    row.hour,
    row.sessions,
    row.attendance,
    row.popularClasses ? row.popularClasses.join(", ") : "",
  ]);

  return [headers, ...rows];
}
